import Box from "@mui/material/Box";
import type {NodeViewProps} from "@tiptap/react";
import {NodeViewWrapper} from "@tiptap/react";
import {
    getVariableChipStyle,
    inlineVariableChipSxFor,
    selectedVariableChipSx,
    unresolvedVariableChipSx,
} from "../../variables/variableChipStyles";
import {useFocusInAnotherEditor} from "./useFocusInAnotherEditor";
import {useVariableCatalogue} from "./useVariableCatalogue";

export function VariableChip({node, editor, getPos, selected}: NodeViewProps) {
    const key = node.attrs.key as string;
    /*
        The editor's store, subscribed, so the chip repaints when the host swaps the catalogue. Not
        `extension.storage`: tiptap builds that afresh on every read, which for a node configured
        without a store means a new empty store each render.
     */
    const catalogue = useVariableCatalogue(editor);
    const known = catalogue.variables.some(variable => variable.key === key);
    // A selection left behind in a field the author has moved on from is not shown as one.
    const focusElsewhere = useFocusInAnotherEditor(editor);
    const lit = selected && !focusElsewhere;
    const markStyle = getVariableChipStyle(node.marks);

    return (
        <NodeViewWrapper as="span">
            <Box
                component="span"
                data-variable={key}
                onClick={() => {
                    const pos = getPos();
                    if (typeof pos === "number") editor.commands.setNodeSelection(pos);
                }}
                sx={{
                    ...inlineVariableChipSxFor(lit),
                    cursor: "default",
                    // The chip is an atom: a caret blinking inside it would promise an edit it cannot take.
                    caretColor: "transparent",
                    /*
                        Hover says a click opens the chip, so only where one can: in an editable editor.
                        It borrows the selected tint but never the ring — the ring means selected, and a
                        hovered chip beside the selected one must not look selected too. A chip coloured
                        by a mark or marked unknown keeps its own background.
                     */
                    ".ProseMirror[contenteditable='true'] &": {cursor: "pointer"},
                    ...(known && !markStyle.backgroundColor && {
                        ".ProseMirror[contenteditable='true'] &:hover": {
                            backgroundColor: selectedVariableChipSx.backgroundColor,
                        },
                    }),
                    ...markStyle,
                    ...(known ? {} : unresolvedVariableChipSx),
                }}
            >
                {key}
            </Box>
        </NodeViewWrapper>
    );
}

export default VariableChip;
